import axios from "axios";
import Api from "./api";
import {
  getViewAccessToken,
  isViewSessionActive,
  notifyViewSessionExpired,
} from "./publicViewSession";

/**
 * Cliente HTTP da visualização pública.
 *
 * Instância separada do `Api` de propósito, por dois motivos:
 * 1. o Authorization aqui é o ViewToken, não o token de usuário — misturar os dois num único
 *    cliente faria o backend receber a credencial errada dependendo da ordem de execução;
 * 2. o interceptor do `Api` trata 401 limpando o localStorage e redirecionando para /signIn.
 *    Para um visitante anônimo isso é um bug: o correto é reabrir o modal de PIN.
 */
const ViewApi = axios.create({
  baseURL: process.env.REACT_APP_API_LOCAL_URL,
});

ViewApi.interceptors.request.use((config) => {
  const token = getViewAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

ViewApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 401 = token ausente/expirado/inválido. 403 = token válido mas fora do escopo (troca de GUID),
    // que não é motivo para invalidar a sessão inteira.
    if (status === 401) notifyViewSessionExpired();

    return Promise.reject(error);
  },
);

/**
 * Escolhe o cliente e a URL conforme o contexto: dentro de uma sessão de visualização pública usa
 * o ViewApi + rota /public/view/*; caso contrário mantém o comportamento autenticado de sempre.
 *
 * Deixa os services com uma linha só de mudança e sem `if` espalhado pelos componentes.
 */
export const resolveViewRoute = (authenticatedPath, publicPath) =>
  isViewSessionActive()
    ? { client: ViewApi, url: publicPath }
    : { client: Api, url: authenticatedPath };

export default ViewApi;
